import React, { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Layout } from './components/Layout'
import { OnboardingRecordEdit } from './components/OnboardingRecordEdit'
import { fetchTenantRegistrationRecord, updateTenantRegistrationRecord } from '../../../apis/registration'
import type { TenantRegistration } from '../../../types/registration'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { SALESFORCE_BASE_URL } from './constants'
import { setOnboardingViewFocus } from '../../chat/onboardingViewFocus'
import { setNotesViewContext } from '../../notes/notesViewContext'

export const OnboardingDetail: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>()
  const dispatch = useAppDispatch()
  const [registration, setRegistration] = useState<TenantRegistration | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!registrationId) return
    try {
      const record = await fetchTenantRegistrationRecord(registrationId)
      setRegistration(record)
    } catch (error: any) {
      console.error('Failed to load registration:', error)
      dispatch(pushToast({ type: 'error', message: 'Failed to load registration', description: error?.message }))
      setRegistration(null)
    }
    setLoading(false)
  }, [registrationId])

  const handleUpdate = useCallback(
    async (reg: TenantRegistration) => {
      try {
        const result = await updateTenantRegistrationRecord(reg.id, reg)
        if (result.result !== 'success') {
          dispatch(
            pushToast({
              type: 'error',
              message: 'Failed to update registration',
            })
          )
          return
        }
        await fetchData()
        dispatch(
          pushToast({
            type: 'success',
            message: 'Registration updated',
          })
        )
      } catch (error: any) {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Failed to update registration',
            description: error.response?.data?.message ?? error.message,
          })
        )
      }
    },
    [fetchData, dispatch]
  )

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!registration || !registrationId || registration.id !== registrationId) {
      setOnboardingViewFocus(null)
      return
    }
    setOnboardingViewFocus({
      registrationId,
      businessName: registration.tenant?.name?.split('-')?.[0]?.trim() || registration.tenant?.name,
      propertyName: registration.property || undefined,
      utilityName: registration.utility || undefined,
    })
    return () => setOnboardingViewFocus(null)
  }, [registration, registrationId])

  useEffect(() => {
    if (!registrationId) {
      setNotesViewContext(null)
      return
    }
    if (!registration || registration.id !== registrationId) {
      setNotesViewContext(null)
      return
    }
    const businessLabel =
      registration.tenant?.name?.split('-')?.[0]?.trim() || registration.tenant?.name
    setNotesViewContext({
      sourceEntity: `registration|${registration.id}`,
      label: businessLabel,
    })
    return () => setNotesViewContext(null)
  }, [registration, registrationId])

  if (loading) {
    return (
      <Layout title="">
        <div className="text-sm">Loading...</div>
      </Layout>
    )
  }

  if (!registration) {
    return (
      <Layout title="Not Found">
        <div className="p-8 text-center">
          <p className="text-slate-500 mb-4">Registration not found.</p>
        </div>
      </Layout>
    )
  }

  const propertyName = registration.property

  return (
    <Layout
      stickyTitle={true}
      title={`Registration: ${registration.tenant?.name}`}
      subtitle={
        <div className="flex items-center gap-4 text-sm">
          {propertyName && (
            <span>
              Property:{' '}
              <Link
                to={`/tenant/onboarding?propertyName=${encodeURIComponent(propertyName)}`}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                {propertyName}
              </Link>
            </span>
          )}
          {registration.utility && (
            <span className="text-slate-500 dark:text-slate-400">
              Utility: <span className="text-slate-700 dark:text-slate-200">{registration.utility}</span>
            </span>
          )}
          {registration.tenant?.salesforceAccountId && (
            <span className="text-slate-500 dark:text-slate-400">
              SF Account:{' '}
              <a
                href={`${SALESFORCE_BASE_URL}/lightning/r/Account/${registration.tenant.salesforceAccountId}/view`}
                target="_blank"
                rel="noopener noreferrer"
                title={registration.tenant.salesforceAccountId}
                className="max-w-[16rem] truncate inline-block align-bottom text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                {registration.tenant.salesforceAccountName || registration.tenant.salesforceAccountId}
              </a>
            </span>
          )}
          {registration.salesforceContactId && (
            <span className="text-slate-500 dark:text-slate-400">
              SF Contact:{' '}
              <a
                href={`${SALESFORCE_BASE_URL}/lightning/r/Contact/${registration.salesforceContactId}/view`}
                target="_blank"
                rel="noopener noreferrer"
                title={registration.salesforceContactId}
                className="max-w-[16rem] truncate inline-block align-bottom text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                {registration.salesforceContactName || registration.salesforceContactId}
              </a>
            </span>
          )}
        </div>
      }
    >
      <OnboardingRecordEdit onboarding={registration} refreshList={fetchData} updateOnboardingFn={handleUpdate} isFullPage />
    </Layout>
  )
}
